import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";
import { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import sentinelIcon from "@/assets/sentinelhub-icon.svg";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations, t, Language } from "@/i18n/translations";
import { scrollToAnchor } from "@/lib/smoothScroll";
import { LANG_PREFIX, localizedPath } from "@/lib/routes";

const LANGUAGES: { code: Language; label: string; flag: string; altText: string }[] = [
  { code: "pt", label: "PT", flag: "https://flagcdn.com/w40/br.png", altText: "Selecione o idioma Português" },
  { code: "es", label: "ES", flag: "https://flagcdn.com/w40/es.png", altText: "Selecciona el idioma Español" },
  { code: "en", label: "EN", flag: "https://flagcdn.com/w40/us.png", altText: "Select English language" },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { lang, setLang } = useLanguage();
  const h = translations.header;
  const location = useLocation();
  const navigate = useNavigate();

  const homePath = localizedPath("home", lang);
  const insightsPath = localizedPath("insights", lang);
  const threatMapPath = localizedPath("threatMap", lang);
  const isOnHome = location.pathname === homePath || location.pathname === homePath.replace(/\/$/, "");

  const NAV_ITEMS: { hash: string; label: string }[] = [
    { hash: "#features", label: t(h.features, lang) },
    { hash: "#how-it-works", label: t(h.howItWorks, lang) },
    { hash: "#dashboard", label: t(h.dashboard, lang) },
    { hash: "#pricing", label: t(h.pricing, lang) },
  ];

  const goTo = (hash: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMenuOpen(false);
    if (isOnHome) {
      scrollToAnchor(hash);
    } else {
      navigate(`${homePath}${hash}`);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link
            to={homePath}
            onClick={(e) => {
              if (isOnHome) {
                e.preventDefault();
                scrollToAnchor("#hero");
              }
            }}
            className="flex shrink-0 items-center gap-2 lg:gap-3"
          >
            <img src={sentinelIcon} alt="SentinelHub Security Platform" className="h-9 w-9 lg:h-12 lg:w-12" />
            <span className="text-lg lg:text-xl font-bold font-display">
              Sentinel<span className="text-primary">Hub</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.hash}
                href={`${homePath}${item.hash}`}
                onClick={goTo(item.hash)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {item.label}
              </a>
            ))}
            <Link to={threatMapPath} className="text-muted-foreground hover:text-foreground transition-colors">
              {t(h.threatMap, lang)}
            </Link>
            <Link to={insightsPath} className="text-muted-foreground hover:text-foreground transition-colors">
              Insights
            </Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <div className="flex items-center gap-1 bg-secondary/40 rounded-lg p-1">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm transition-all ${
                    lang === l.code
                      ? "bg-primary/20 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                  }`}
                  aria-label={l.altText}
                >
                  <img src={l.flag} alt={l.altText} className="w-5 h-3.5 object-cover rounded-[2px]" />
                  <span className="text-xs">{l.label}</span>
                </button>
              ))}
            </div>

            <a href={`${homePath}#contact`} onClick={goTo("#contact")}>
              <Button variant="hero">{t(h.contact, lang)}</Button>
            </a>
          </div>

          {/* Mobile */}
          <div className="lg:hidden flex items-center gap-1.5">
            <div className="flex items-center gap-0.5 bg-secondary/40 rounded-lg p-0.5">
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-1.5 py-1.5 rounded-md text-base transition-all ${
                    lang === l.code ? "bg-primary/20" : "hover:bg-secondary/50"
                  }`}
                  aria-label={l.altText}
                >
                  <img src={l.flag} alt={l.altText} className="w-5 h-3.5 object-cover rounded-[2px]" />
                </button>
              ))}
            </div>
            <button
              className="text-foreground"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 flex flex-col gap-4">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.hash}
                href={`${homePath}${item.hash}`}
                onClick={goTo(item.hash)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                {item.label}
              </a>
            ))}
            <Link to={threatMapPath} onClick={() => setIsMenuOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors">
              {t(h.threatMap, lang)}
            </Link>
            <Link to={insightsPath} onClick={() => setIsMenuOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors">
              Insights
            </Link>
            <div className="mt-4">
              <a href={`${homePath}#contact`} onClick={goTo("#contact")}>
                <Button variant="hero" className="w-full">{t(h.contact, lang)}</Button>
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
